import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, getErrorMessage } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import type { CollectionCompany } from "./entryStatus.ts";
import SearchField from "../../../components/SearchField.tsx";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue.ts";

interface LinkCompanyDialogProps {
  listId: string;
  /** An unresolved entry; null closes the dialog. */
  entry: CollectionCompany | null;
  onClose: () => void;
  /** After the link: the prospect may have folded into the company's. */
  onLinked?: () => void;
}

interface Candidate {
  id: string;
  name: string;
  detail: string;
  hint?: string;
}

const MATCHED_ON: Record<string, string> = {
  org_number: "Same org number",
  domain: "Same domain",
  name: "Similar name",
};

/** Link an entry the database did not hold to the company it is. */
export default function LinkCompanyDialog({ listId, entry, onClose, onLinked }: LinkCompanyDialogProps) {
  const open = !!entry;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {entry && (
        <LinkCompanyBody key={entry.id} listId={listId} entry={entry} onClose={onClose} onLinked={onLinked} />
      )}
    </Dialog>
  );
}

function LinkCompanyBody({
  listId,
  entry,
  onClose,
  onLinked,
}: {
  listId: string;
  entry: CollectionCompany;
  onClose: () => void;
  onLinked?: () => void;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState(entry.company_name ?? "");
  const debounced = useDebouncedValue(search.trim(), 300);

  const suggestions = useQuery({
    queryKey: keys.prospectList.linkSuggestions(listId, entry.id),
    queryFn: () => api.getLinkSuggestions(listId, entry.id),
  });
  const searchParams = {
    name: debounced,
    country: entry.company_country ? [entry.company_country] : undefined,
    limit: 8,
  };
  const results = useQuery({
    queryKey: keys.masterCompany.list(searchParams),
    queryFn: () => api.getMasterCompanies(searchParams),
    enabled: debounced.length >= 2,
  });

  const link = useMutation({
    mutationFn: (companyId: string) => api.linkProspect(listId, entry.id, companyId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.prospectList.detail(listId) });
      await queryClient.invalidateQueries({
        queryKey: keys.prospectListContactEntry.byList(listId),
      });
      // Linking re-keys the company's prospect, or folds it into the company's.
      await queryClient.invalidateQueries({ queryKey: keys.prospect.all });
      onClose();
      onLinked?.();
    },
  });

  const suggested: Candidate[] = (suggestions.data ?? []).map((s) => ({
    id: s.master_company_id,
    name: s.name,
    detail: [s.country, s.org_number, s.domain].filter(Boolean).join(" · "),
    hint: MATCHED_ON[s.matched_on],
  }));
  const suggestedIds = new Set(suggested.map((c) => c.id));
  const found: Candidate[] = (results.data?.items ?? [])
    .filter((c) => !suggestedIds.has(c.id))
    .map((c) => ({
      id: c.id,
      name: c.name,
      detail: [c.country, c.org_number, c.industry].filter(Boolean).join(" · "),
    }));
  const known = [entry.company_country, entry.company_org_number, entry.company_website]
    .filter(Boolean)
    .join(" · ");

  const renderList = (items: Candidate[]) => (
    <List dense disablePadding>
      {items.map((c) => (
        <ListItemButton key={c.id} onClick={() => link.mutate(c.id)} disabled={link.isPending}>
          <ListItemText primary={c.name} secondary={c.detail || undefined} />
          {c.hint && <Chip size="small" variant="outlined" label={c.hint} />}
        </ListItemButton>
      ))}
    </List>
  );

  return (
    <>
      <DialogTitle>Link {entry.company_name ?? "company"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Not matched to a company in the database{known ? ` (${known})` : ""}. Pick the company
            it is; if the collection already has it, the two entries merge.
          </Typography>
          {suggestions.isLoading ? (
            <CircularProgress size={20} />
          ) : suggested.length > 0 ? (
            <div>
              <Typography variant="subtitle2">Suggestions</Typography>
              {renderList(suggested)}
            </div>
          ) : null}
          <div>
            <SearchField value={search} onChange={setSearch} placeholder="Search companies" fullWidth clearable />
            <Typography variant="caption" component="p" sx={{ color: "text.secondary", mt: 0.5, mx: 1.75 }}>
              {entry.company_country ? `In ${entry.company_country}` : "In every country"}
            </Typography>
          </div>
          {results.isFetching && <CircularProgress size={20} />}
          {found.length > 0 && renderList(found)}
          {!results.isFetching && debounced.length >= 2 && results.data && found.length === 0 && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No other companies match.
            </Typography>
          )}
          {link.isError && <Alert severity="error">{getErrorMessage(link.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </>
  );
}
